import React from 'react';
import { Container, Card, Table, Badge, Button, Modal, Form, Row, Col, Spinner, Alert } from 'react-bootstrap';
import {
  UserPlusIcon,
  PencilSquareIcon,
  TrashIcon,
  EyeIcon,
  EyeSlashIcon,
  UserGroupIcon,
} from '@heroicons/react/24/solid';
import CompanyLayout from '../common/CompanyLayout';
import { useCompanyCustomers } from './hooks/useCompanyCustomers';

const CompanyCustomers: React.FC = () => {
  const {
    isExpired,
    navigate,
    customers,
    loading,
    showModal,
    editingId,
    showPassword,
    form,
    setForm,
    saving,
    error,
    success,
    customerLimit,
    canAddCustomer,
    addDisabled,
    addTitle,
    handleOpenCreate,
    handleOpenEdit,
    handleSave,
    handleDelete,
    closeModal,
    toggleShowPassword,
    clearSuccess,
  } = useCompanyCustomers();

  return (
    <CompanyLayout>
      <Container fluid className="py-4 px-3 px-md-4 page-content-padding">
        <div className="d-flex flex-wrap justify-content-between align-items-center mb-4">
          <div className="d-flex align-items-center gap-2 mb-2 mb-md-0">
            <div style={{ 
              backgroundColor: '#ecfdf5', 
              borderRadius: '8px', 
              padding: '6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <UserGroupIcon style={{ width: '22px', height: '22px', color: '#059669' }} />
            </div>
            <h4 className="mb-0 fw-bold" style={{ color: '#1e293b' }}>My Customers</h4>
            <Badge 
              bg="" 
              style={{ 
                backgroundColor: '#ecfdf5', 
                color: '#059669', 
                fontSize: '0.75rem',
                fontWeight: '600',
                padding: '4px 10px',
                borderRadius: '6px'
              }}
            >
              {customers.length}{customerLimit > 0 ? `/${customerLimit}` : ''}
            </Badge>
          </div>
          <Button size="sm" variant="success" className="d-flex align-items-center gap-1" onClick={handleOpenCreate} disabled={addDisabled} title={addTitle} style={{ padding: '6px 14px' }}>
            <UserPlusIcon style={{ width: '15px', height: '15px' }} />
            <span className="d-none d-sm-inline">Add Customer</span>
          </Button>
        </div>

        {success && <Alert variant="success" dismissible onClose={clearSuccess} className="py-2">{success}</Alert>}

        {!canAddCustomer && !isExpired && (
          <Alert variant="warning" className="py-2 d-flex align-items-center justify-content-between flex-wrap gap-2" style={{ fontSize: '0.85rem' }}>
            <span>
              You've reached the <strong>{customerLimit}-client limit</strong> on your current plan.
            </span>
            <Button size="sm" variant="warning" onClick={() => navigate('/pricing')} style={{ fontWeight: 600, fontSize: '0.8rem' }}>
              Upgrade Plan
            </Button>
          </Alert>
        )}

        {loading ? (
          <div className="text-center py-5"><Spinner animation="border" variant="success" /></div>
        ) : customers.length === 0 ? (
          <Card className="border-0 shadow-sm text-center py-5">
            <Card.Body>
              <UserGroupIcon style={{ width: '48px', height: '48px', color: '#d1d5db' }} />
              <p className="text-muted mt-2">No customers yet. Add your first customer to get started.</p>
              <Button variant="success" onClick={handleOpenCreate} disabled={addDisabled}>Add Customer</Button>
            </Card.Body>
          </Card>
        ) : (
          <Card className="border-0 shadow-sm" style={{ borderRadius: '10px' }}>
            <Card.Body className="p-0">
              <div className="table-responsive">
                <Table hover className="mb-0 align-middle" style={{ fontSize: '0.85rem' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                      <th style={{ color: '#475569', fontWeight: '600', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '12px 16px' }}>Company</th>
                      <th style={{ color: '#475569', fontWeight: '600', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '12px 16px' }} className="d-none d-md-table-cell">Contact</th>
                      <th style={{ color: '#475569', fontWeight: '600', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '12px 16px' }} className="d-none d-lg-table-cell">City</th>
                      <th style={{ color: '#475569', fontWeight: '600', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '12px 16px' }}>Orders</th>
                      <th style={{ color: '#475569', fontWeight: '600', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '12px 16px' }} className="d-none d-sm-table-cell">Total Spent</th>
                      <th style={{ color: '#475569', fontWeight: '600', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '12px 16px' }} className="d-none d-md-table-cell">Amount Due</th>
                      <th style={{ color: '#475569', fontWeight: '600', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '12px 16px' }}>Status</th>
                      <th style={{ color: '#475569', fontWeight: '600', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '12px 16px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customers.map((c) => (
                      <tr key={c.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '12px 16px' }}>
                          <div className="fw-semibold" style={{ color: '#1e293b' }}>{c.company_name}</div>
                          <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{c.customer_id}</div>
                        </td>
                        <td className="d-none d-md-table-cell" style={{ padding: '12px 16px' }}>
                          <div style={{ color: '#1e293b' }}>{c.contact_person}</div>
                          <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{c.phone_number}</div>
                        </td>
                        <td className="d-none d-lg-table-cell" style={{ color: '#64748b', padding: '12px 16px' }}>{c.city || '-'}</td>
                        <td style={{ padding: '12px 16px' }}>
                          <div className="d-flex align-items-center gap-2">
                            <span style={{ 
                              fontSize: '0.9rem',
                              fontWeight: '700',
                              color: '#1e293b',
                              backgroundColor: '#f1f5f9',
                              padding: '2px 10px',
                              borderRadius: '4px',
                              minWidth: '28px',
                              textAlign: 'center',
                              display: 'inline-block'
                            }}>
                              {c.total_orders}
                            </span>
                            {c.pending_orders > 0 && (
                              <span style={{ 
                                fontSize: '0.7rem', 
                                fontWeight: '600',
                                backgroundColor: '#fef3c7', 
                                color: '#92400e', 
                                padding: '2px 8px', 
                                borderRadius: '4px',
                                border: '1px solid #fde68a',
                                whiteSpace: 'nowrap'
                              }}>
                                {c.pending_orders} pending
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="d-none d-sm-table-cell" style={{ color: '#1e293b', padding: '12px 16px' }}>
                          ${c.total_spent.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                        </td>
                        <td className="d-none d-md-table-cell" style={{ padding: '12px 16px' }}>
                          <span style={{ color: c.amount_due > 0 ? '#dc2626' : '#059669', fontWeight: '600' }}>
                            ${c.amount_due.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                          </span>
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <span style={{ 
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            padding: '3px 10px',
                            borderRadius: '4px',
                            backgroundColor: c.is_active ? '#ecfdf5' : '#f1f5f9',
                            color: c.is_active ? '#059669' : '#64748b'
                          }}>
                            {c.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <div className="d-flex gap-1">
                            <Button size="sm" variant="outline-success"
                              onClick={() => navigate(`/company/customers/${c.id}`)}
                              title="View customer details"
                              style={{ padding: '4px 8px', lineHeight: 1 }}>
                              <EyeIcon style={{ width: '14px', height: '14px' }} />
                            </Button>
                            <Button size="sm" variant="outline-success"
                              onClick={() => handleOpenEdit(c.id)} disabled={isExpired}
                              title="Edit customer"
                              style={{ padding: '4px 8px', lineHeight: 1 }}>
                              <PencilSquareIcon style={{ width: '14px', height: '14px' }} />
                            </Button>
                            <Button size="sm" variant="outline-danger"
                              onClick={() => handleDelete(c.id, c.company_name)} disabled={isExpired}
                              title="Delete customer"
                              style={{ padding: '4px 8px', lineHeight: 1 }}>
                              <TrashIcon style={{ width: '14px', height: '14px' }} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        )}

        {/* Add/Edit Customer Modal */}
        <Modal show={showModal} onHide={closeModal} size="lg" centered>
          <Modal.Header closeButton style={{ backgroundColor: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
            <Modal.Title style={{ color: '#1e293b', fontSize: '1.1rem' }}>
              <UserGroupIcon style={{ width: '20px', height: '20px', marginRight: '8px', color: '#059669' }} />
              {editingId ? 'Edit Customer' : 'Add New Customer'}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {error && <Alert variant="danger" className="py-2">{error}</Alert>}
            <Row className="g-3">
              <Col xs={12} md={6}>
                <Form.Group>
                  <Form.Label className="small fw-semibold">Username *</Form.Label>
                  <Form.Control size="sm" value={form.username} disabled={!!editingId}
                    onChange={(e) => setForm({ ...form, username: e.target.value })}
                    placeholder="Login username" />
                </Form.Group>
              </Col>
              <Col xs={12} md={6}>
                <Form.Group>
                  <Form.Label className="small fw-semibold">Email</Form.Label>
                  <Form.Control size="sm" type="email" value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })} />
                </Form.Group>
              </Col>
              <Col xs={12} md={6}>
                <Form.Group>
                  <Form.Label className="small fw-semibold">Company Name *</Form.Label>
                  <Form.Control size="sm" value={form.company_name}
                    onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                    placeholder="Business name" />
                </Form.Group>
              </Col>
              <Col xs={12} md={6}>
                <Form.Group>
                  <Form.Label className="small fw-semibold">Contact Person</Form.Label>
                  <Form.Control size="sm" value={form.contact_person}
                    onChange={(e) => setForm({ ...form, contact_person: e.target.value })} />
                </Form.Group>
              </Col>
              <Col xs={12} md={6}>
                <Form.Group>
                  <Form.Label className="small fw-semibold">Phone</Form.Label>
                  <Form.Control size="sm" value={form.phone_number}
                    onChange={(e) => setForm({ ...form, phone_number: e.target.value })} />
                </Form.Group>
              </Col>
              <Col xs={12} md={6}>
                <Form.Group>
                  <Form.Label className="small fw-semibold">City</Form.Label>
                  <Form.Control size="sm" value={form.city}
                    onChange={(e) => setForm({ ...form, city: e.target.value })} />
                </Form.Group>
              </Col>
              <Col xs={12}>
                <Form.Group>
                  <Form.Label className="small fw-semibold">Address</Form.Label>
                  <Form.Control size="sm" as="textarea" rows={2} value={form.address}
                    onChange={(e) => setForm({ ...form, address: e.target.value })} />
                </Form.Group>
              </Col>
              <Col xs={6} md={4}>
                <Form.Group>
                  <Form.Label className="small fw-semibold">Postal Code</Form.Label>
                  <Form.Control size="sm" value={form.postal_code}
                    onChange={(e) => setForm({ ...form, postal_code: e.target.value })} />
                </Form.Group>
              </Col>
              <Col xs={6} md={4}>
                <Form.Group>
                  <Form.Label className="small fw-semibold">Discount %</Form.Label>
                  <Form.Control size="sm" type="number" step="0.01" min="0" max="99.99"
                    value={form.discount_percentage}
                    onChange={(e) => setForm({ ...form, discount_percentage: e.target.value })} />
                </Form.Group>
              </Col>
              <Col xs={12} md={4}>
                <Form.Group>
                  <Form.Label className="small fw-semibold">Credit Limit</Form.Label>
                  <Form.Control size="sm" type="number" step="0.01" min="0"
                    value={form.total_credit_limit}
                    onChange={(e) => setForm({ ...form, total_credit_limit: e.target.value })} />
                </Form.Group>
              </Col>
              <Col xs={12}>
                <Form.Group>
                  <Form.Label className="small fw-semibold">
                    {editingId ? 'New Password (leave blank to keep current)' : 'Password *'}
                  </Form.Label>
                  <div style={{ position: 'relative' }}>
                    <Form.Control size="sm"
                      type={showPassword ? 'text' : 'password'} value={form.password}
                      onChange={(e) => setForm({ ...form, password: e.target.value })}
                      required={!editingId}
                      placeholder={editingId ? '••••••' : 'Min 6 characters'}
                      style={{ paddingRight: '36px' }} />
                    <button type="button" tabIndex={-1} onClick={toggleShowPassword}
                      style={{
                        position: 'absolute', right: '1px', top: '1px', bottom: '1px',
                        width: '34px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: 'transparent', border: 'none', cursor: 'pointer',
                        borderRadius: '0 5px 5px 0',
                      }}>
                      {showPassword
                        ? <EyeSlashIcon style={{ width: '14px', height: '14px', color: '#64748b' }} />
                        : <EyeIcon style={{ width: '14px', height: '14px', color: '#64748b' }} />}
                    </button>
                  </div>
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" size="sm" onClick={closeModal}>Cancel</Button>
            <Button variant="success" size="sm" onClick={handleSave} disabled={saving}>
              {saving ? <Spinner size="sm" animation="border" /> : (editingId ? 'Update' : 'Create')}
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </CompanyLayout>
  );
};

export default CompanyCustomers;
